/**
 * AgentTraceView - Visualizes agentic process for text-to-SQL
 *
 * Shows the step-by-step agent workflow similar to LangSmith/ADK:
 * - Intent Classification
 * - Model Selection
 * - SQL Generation (streaming)
 * - Query Execution
 * - Results Processing
 *
 * Dark glassmorphism design with ADK thought visualization
 */

import { useState, useEffect, useRef } from 'react';
import { Badge } from '../ui/badge';

// Step type definitions with dark glassmorphism colors
// ADK Multi-Agent: Enhanced step types for tool calls, video analysis, and charts
const STEP_TYPES = {
  user: { icon: '👤', borderColor: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.15)', label: 'User Query' },
  intent: { icon: '🎯', borderColor: '#A855F7', bgColor: 'rgba(168, 85, 247, 0.15)', label: 'Intent Classification' },
  model: { icon: '🤖', borderColor: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.15)', label: 'Model Selection' },
  sql: { icon: '📊', borderColor: '#6366F1', bgColor: 'rgba(99, 102, 241, 0.15)', label: 'SQL Generation' },
  execute: { icon: '⚡', borderColor: '#EAB308', bgColor: 'rgba(234, 179, 8, 0.15)', label: 'Query Execution' },
  results: { icon: '✅', borderColor: '#22C55E', bgColor: 'rgba(34, 197, 94, 0.15)', label: 'Results' },
  error: { icon: '❌', borderColor: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.15)', label: 'Error' },
  thinking: { icon: '💭', borderColor: '#06B6D4', bgColor: 'rgba(6, 182, 212, 0.15)', label: 'Thinking' },
  // ADK Multi-Agent: New step types
  tool_call: { icon: '🔧', borderColor: '#F97316', bgColor: 'rgba(249, 115, 22, 0.15)', label: 'Tool Call' },
  tool_result: { icon: '📦', borderColor: '#14B8A6', bgColor: 'rgba(20, 184, 166, 0.15)', label: 'Tool Result' },
  reasoning: { icon: '🧠', borderColor: '#EC4899', bgColor: 'rgba(236, 72, 153, 0.15)', label: 'Reasoning' },
  video: { icon: '🎬', borderColor: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.15)', label: 'Video Analysis' },
  chart: { icon: '📈', borderColor: '#10B981', bgColor: 'rgba(16, 185, 129, 0.15)', label: 'Chart Generation' },
  news: { icon: '📰', borderColor: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.15)', label: 'News Fetch' },
  scores: { icon: '🏆', borderColor: '#0EA5E9', bgColor: 'rgba(14, 165, 233, 0.15)', label: 'Live Scores' },
  standings: { icon: '🏅', borderColor: '#84CC16', bgColor: 'rgba(132, 204, 22, 0.15)', label: 'Standings' },
};

// Individual step card component with dark glassmorphism
function StepCard({ step, isExpanded, onToggle, isLatest }) {
  const typeInfo = STEP_TYPES[step.type] || STEP_TYPES.thinking;
  const hasContent = step.content || step.sql || step.data || step.thought || step.input || step.output;

  return (
    <div
      className={`relative pl-8 pb-4 ${!isLatest ? 'ml-3' : 'ml-3'}`}
      style={{
        borderLeft: !isLatest ? '2px solid rgba(255, 255, 255, 0.1)' : 'none'
      }}
    >
      {/* Timeline dot */}
      <div
        className="absolute left-0 -translate-x-1/2 w-7 h-7 rounded-full flex items-center justify-center text-sm"
        style={{
          background: step.type === 'error'
            ? 'rgba(239, 68, 68, 0.8)'
            : 'rgba(13, 13, 18, 0.9)',
          border: `2px solid ${typeInfo.borderColor}`,
          boxShadow: step.streaming
            ? `0 0 12px ${typeInfo.borderColor}`
            : `0 0 6px ${typeInfo.borderColor}40`,
          animation: step.streaming ? 'pulse 2s infinite' : 'none'
        }}
      >
        {typeInfo.icon}
      </div>

      {/* Step content */}
      <div
        className="rounded-xl p-4 transition-all cursor-pointer backdrop-blur-md"
        style={{
          background: typeInfo.bgColor,
          borderLeft: `4px solid ${typeInfo.borderColor}`,
          border: `1px solid rgba(255, 255, 255, 0.08)`,
          borderLeftWidth: '4px',
          borderLeftColor: typeInfo.borderColor,
          boxShadow: isExpanded
            ? `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px ${typeInfo.borderColor}20`
            : '0 4px 16px rgba(0, 0, 0, 0.2)'
        }}
        onClick={onToggle}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-white">{typeInfo.label}</span>
            {step.streaming && (
              <Badge
                variant="outline"
                className="text-xs animate-pulse border-green-500/50 text-green-400 bg-green-500/10"
              >
                streaming...
              </Badge>
            )}
            {step.model && (
              <Badge
                variant="secondary"
                className="text-xs bg-white/10 text-gray-300 border-white/20"
              >
                {step.model.replace('-preview', '')}
              </Badge>
            )}
          </div>
          {step.duration && (
            <span className="text-xs font-mono text-gray-400">
              {step.duration}ms
            </span>
          )}
        </div>

        {/* Brief preview */}
        <div className="text-sm text-gray-200 truncate">
          {step.label || step.text || (step.sql && 'SQL Query Generated') || (step.thought && 'Agent reasoning...')}
        </div>

        {/* ADK Thought/Reasoning bubble */}
        {step.thought && !isExpanded && (
          <div className="mt-2 text-xs text-cyan-400/80 italic truncate">
            💭 {step.thought.substring(0, 100)}...
          </div>
        )}

        {/* Expanded content */}
        {isExpanded && hasContent && (
          <div className="mt-4 space-y-3">
            {/* ADK Agent Thought - Primary display */}
            {step.thought && (
              <div
                className="p-3 rounded-lg"
                style={{
                  background: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)'
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-cyan-400">💭</span>
                  <span className="text-xs font-semibold text-cyan-400">Agent Thought</span>
                </div>
                <p className="text-sm text-cyan-100 leading-relaxed whitespace-pre-wrap">
                  {step.thought}
                </p>
              </div>
            )}

            {/* SQL content */}
            {step.sql && (
              <pre
                className="p-3 rounded-lg text-xs overflow-x-auto font-mono"
                style={{
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(34, 197, 94, 0.3)'
                }}
              >
                <code className="text-green-400">{step.sql}</code>
                {step.streaming && <span className="animate-pulse text-green-300">▋</span>}
              </pre>
            )}

            {/* General content */}
            {step.content && (
              <div
                className="text-sm p-3 rounded-lg text-gray-200"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {step.content}
              </div>
            )}

            {/* Tool input/output - ADK style */}
            {step.input && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-orange-400">🔧</span>
                  <span className="text-xs font-semibold text-orange-400">Tool Input</span>
                </div>
                <pre
                  className="p-2 rounded-lg text-xs overflow-x-auto text-orange-200 font-mono"
                  style={{
                    background: 'rgba(249, 115, 22, 0.1)',
                    border: '1px solid rgba(249, 115, 22, 0.2)'
                  }}
                >
                  {JSON.stringify(step.input, null, 2)}
                </pre>
              </div>
            )}
            {step.output && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-teal-400">📦</span>
                  <span className="text-xs font-semibold text-teal-400">Tool Output</span>
                </div>
                <pre
                  className="p-2 rounded-lg text-xs overflow-x-auto max-h-40 text-teal-200 font-mono"
                  style={{
                    background: 'rgba(20, 184, 166, 0.1)',
                    border: '1px solid rgba(20, 184, 166, 0.2)'
                  }}
                >
                  {typeof step.output === 'string'
                    ? step.output
                    : JSON.stringify(step.output, null, 2)}
                </pre>
              </div>
            )}

            {/* Results count */}
            {step.data && (
              <div className="flex items-center gap-2 text-xs text-green-400">
                <span>✅</span>
                <span className="font-semibold">{step.data.length} results returned</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Main trace view component
export default function AgentTraceView({ steps, isStreaming }) {
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const containerRef = useRef(null);

  // Auto-expand latest step
  useEffect(() => {
    if (steps.length > 0) {
      const latestStep = steps[steps.length - 1];
      setExpandedSteps(prev => new Set([...prev, latestStep.id]));
    }
  }, [steps]);

  // Auto-scroll to bottom when new steps added
  useEffect(() => {
    if (containerRef.current && isStreaming) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [steps, isStreaming]);

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  if (steps.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="max-h-[500px] overflow-y-auto pr-2 rounded-xl p-4"
      style={{
        background: 'rgba(13, 13, 18, 0.8)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.08)'
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between mb-4 pb-3"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🔍</span>
          <h3 className="font-semibold text-sm text-white">ADK Agent Trace</h3>
        </div>
        <Badge
          variant="outline"
          className="text-xs bg-white/5 text-gray-300 border-white/20"
        >
          {steps.length} step{steps.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Timeline */}
      <div className="relative">
        {steps.map((step, idx) => (
          <StepCard
            key={step.id}
            step={step}
            isExpanded={expandedSteps.has(step.id)}
            onToggle={() => toggleStep(step.id)}
            isLatest={idx === steps.length - 1}
          />
        ))}
      </div>

      {/* Processing indicator */}
      {isStreaming && (
        <div
          className="flex items-center gap-2 ml-3 pl-8 py-3 text-sm font-medium text-cyan-400"
        >
          <span className="animate-spin">⚾</span>
          <span>Processing...</span>
          <span className="animate-pulse text-xs text-gray-500">Agent working...</span>
        </div>
      )}
    </div>
  );
}
